import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChapterText, ContinueButton, Revelation, Whisper } from '../zen-ui';
import { NOTE_ORIGIN, RELAYS } from './field';

interface Mote {
  dx: number;
  dy: number;
  delay: number;
}

/** Chapter III — a note doesn't live in one place. Release it to the relays. */
export function ReleaseNote({ onComplete }: { onComplete: () => void }) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [motes, setMotes] = useState<Mote[]>([]);
  const [flying, setFlying] = useState(false);
  const [litCount, setLitCount] = useState(0);
  const released = motes.length > 0;
  const allLit = litCount >= RELAYS.length;

  useEffect(() => {
    if (!released) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setFlying(true));
    });
    const timers = RELAYS.map((_, i) =>
      window.setTimeout(
        () => setLitCount((c) => Math.min(RELAYS.length, c + 1)),
        1500 + i * 160,
      ),
    );
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      timers.forEach(clearTimeout);
    };
  }, [released]);

  const release = () => {
    if (released) return;
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    setMotes(
      RELAYS.map((r, i) => ({
        dx: ((r.x - NOTE_ORIGIN.x) / 100) * rect.width,
        dy: ((r.y - NOTE_ORIGIN.y) / 100) * rect.height,
        delay: i * 160,
      })),
    );
  };

  return (
    <div className="relative z-10 flex min-h-dvh flex-col items-center justify-center gap-10 px-6 py-24 text-center">
      <ChapterText numeral="III · the relays" title="your words live everywhere.">
        Your note doesn't live in one place. It drifts to relays — quiet post
        offices that keep copies and pass them along.
      </ChapterText>

      <div
        ref={boxRef}
        className="animate-zen-fade-in relative mx-auto h-72 w-full max-w-2xl sm:h-80"
        style={{ animationDelay: '700ms' }}
      >
        {RELAYS.map((r, i) => (
          <div
            key={i}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${r.x}%`, top: `${r.y}%` }}
          >
            <div
              className={cn(
                'grid h-12 w-12 place-items-center rounded-full border transition-all duration-1000',
                i < litCount
                  ? 'border-amber-100/40 bg-amber-100/5 shadow-[0_0_30px_rgba(253,224,171,0.12)]'
                  : 'border-white/15',
              )}
            >
              <div
                className={cn(
                  'h-2 w-2 rounded-full transition-all duration-1000',
                  i < litCount
                    ? 'bg-amber-200 shadow-[0_0_16px_rgba(253,224,171,0.9)]'
                    : 'bg-white/20',
                )}
              />
            </div>
          </div>
        ))}

        {/* the note, waiting to be released */}
        <div
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${NOTE_ORIGIN.x}%`, top: `${NOTE_ORIGIN.y}%` }}
        >
          <button
            type="button"
            onClick={release}
            disabled={released}
            aria-label="release the note to the relays"
            className={cn(
              'zen-glass rounded-full px-5 py-2.5 font-serif text-sm italic text-slate-200/90 transition-all duration-700',
              released
                ? 'scale-50 opacity-0'
                : 'cursor-pointer hover:border-amber-100/40 hover:shadow-[0_0_30px_rgba(253,224,171,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-100/60',
            )}
          >
            “the river is quiet tonight.”
          </button>
        </div>

        {/* the copies, drifting outward */}
        {motes.map((m, i) => (
          <div
            key={i}
            aria-hidden="true"
            className="absolute h-1.5 w-1.5 rounded-full bg-amber-200 shadow-[0_0_10px_rgba(253,224,171,0.9)] transition-transform duration-[1700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{
              left: `${NOTE_ORIGIN.x}%`,
              top: `${NOTE_ORIGIN.y}%`,
              transform: flying
                ? `translate(calc(-50% + ${m.dx}px), calc(-50% + ${m.dy}px))`
                : 'translate(-50%, -50%)',
              transitionDelay: `${m.delay}ms`,
            }}
          />
        ))}
      </div>

      {!allLit ? (
        <Whisper delay={1100}>
          {released ? 'drifting outward…' : 'touch the note to release it'}
        </Whisper>
      ) : (
        <div className="flex flex-col items-center gap-8">
          <Revelation delay={300}>
            five copies, kept by strangers, carried freely.
          </Revelation>
          <ContinueButton onClick={onComplete} delay={1100} />
        </div>
      )}
    </div>
  );
}
